import React, { useState, useEffect } from 'react';
import {
    MonitorCog , SquareActivity , Tickets,  Menu, X, Home, Package, FileText, Calendar, Users, BarChart3, Settings,
    Bell, Search, ChevronDown, ArrowRight, TrendingUp, TrendingDown,
    Building, Hammer, Truck, Star, AlertCircle, CheckCircle, Clock,
    DollarSign, Activity, Globe, Zap, Target, Award, Shield, LogOut,
    ChevronRight, Filter, Download, RefreshCw, MoreVertical, Plus, User,
    Edit, Trash2, Eye, Package2, Layers, Wrench, ShoppingCart, Box, Hash,
    QrCode, Scan, Database, Copy, ExternalLink, BarChart2, Grid3X3, Cable,
    UserCog, Calculator, Construction, Beaker, Leaf, ShieldCheck,
    HelpCircle, MessageSquare, Phone, Mail, Headphones, Send, Upload,
    FileText as FileIcon, Paperclip, Monitor, Smartphone, AlertTriangle,
    LifeBuoy, BookOpen, MessageCircle, Video, Mic, Camera, MapPin,
    Clock as ClockIcon, CheckCircle2, XCircle, Loader2, Folder , Receipt , Briefcase
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../Styles/dashboard.css';
import ApiService from '../services/api';

import MepIcon from '../assets/MEP_Solutions.png';
import architecturalIcon from '../assets/architectural_icon.png';
import civilIcon from '../assets/civil-icon.png';
import constructionIcon from '../assets/construction-icon.png';
import powerToolsIcon from '../assets/power-tools_icon.png';
import sustainableIcon from '../assets/sustainable-icon.png';
import hygienicIcon from '../assets/hygienic-icon.png';

const TechnicalSupportPage = () => {
    const [user, setUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeMenuItem, setActiveMenuItem] = useState('technical-support');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();


    // Support form states
    const [selectedCategory, setSelectedCategory] = useState('');
    const [priority, setPriority] = useState('medium');
    const [contactMethod, setContactMethod] = useState('email');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [submittedTicketId, setSubmittedTicketId] = useState('');

    const [formData, setFormData] = useState({
        subject: '',
        description: '',
        systemInfo: {
            browser: '',
            operatingSystem: '',
            screenResolution: ''
        },
        contactInfo: {
            phone: '',
            preferredTime: '',
            timezone: 'UTC+3'
        }
    });

    const [attachments, setAttachments] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Support categories
    const supportCategories = [
        {
            id: 'technical-issue',
            name: 'Technical Issue',
            icon: <Monitor className="w-6 h-6" />,
            color: '#dc2626',
            description: 'System errors, bugs, or technical malfunctions',
            urgency: 'high'
        },
        {
            id: 'account-access',
            name: 'Account & Access',
            icon: <Shield className="w-6 h-6" />,
            color: '#059669',
            description: 'Login issues, permissions, or account management',
            urgency: 'high'
        },
        {
            id: 'feature-request',
            name: 'Feature Request',
            icon: <Zap className="w-6 h-6" />,
            color: '#7c3aed',
            description: 'New feature suggestions or enhancements',
            urgency: 'low'
        },
        {
            id: 'data-management',
            name: 'Data Management',
            icon: <Database className="w-6 h-6" />,
            color: '#f59e0b',
            description: 'Issues with data import/export, synchronization',
            urgency: 'medium'
        },
        {
            id: 'training-support',
            name: 'Training & Documentation',
            icon: <BookOpen className="w-6 h-6" />,
            color: '#06b6d4',
            description: 'Need help using features or understanding workflows',
            urgency: 'low'
        },
        {
            id: 'system-performance',
            name: 'Performance Issues',
            icon: <Activity className="w-6 h-6" />,
            color: '#ef4444',
            description: 'Slow loading, timeouts, or system performance problems',
            urgency: 'medium'
        },
        {
            id: 'integration-api',
            name: 'API & Integrations',
            icon: <Cable className="w-6 h-6" />,
            color: '#8b5cf6',
            description: 'Third-party integrations or API-related issues',
            urgency: 'medium'
        },
        {
            id: 'other',
            name: 'Other',
            icon: <HelpCircle className="w-6 h-6" />,
            color: '#6b7280',
            description: 'General inquiries or issues not covered above',
            urgency: 'low'
        }
    ];

    // Load user data on mount
    useEffect(() => {
        const syncUser = ApiService.getUserSync();
        if (syncUser) {
            setUser(syncUser);
            // Auto-detect system information
            setFormData(prev => ({
                ...prev,
                systemInfo: {
                    browser: navigator.userAgent.split(' ').pop(),
                    operatingSystem: navigator.platform,
                    screenResolution: `${screen.width}x${screen.height}`
                }
            }));
        }
    }, []);

    // Time update effect
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Menu items
    const menuItems = [
        {
            id: 'barcoding',
            label: 'Barcoding Database',
            icon: FileText,
            path: '/barcoding'
        },
        {
            id: 'mep-solutions',
            label: 'MEP Solutions',
            icon: MepIcon,
            isImage: true,
            path: '/mep_solutions'
        },
        {
            id: 'architectural-solutions',
            label: 'Architectural Solutions',
            icon: architecturalIcon,
            isImage: true,
            path: '/architectural_solutions'
        },
        {
            id: 'civil-solutions',
            label: 'Civil Solutions',
            icon: civilIcon,
            isImage: true,
            path: '/civil_solutions'
        },
        {
            id: 'construction-chemicals',
            label: 'Construction Chemicals',
            icon: constructionIcon,
            isImage: true,
            path: '/construction_chemicals'
        },
        {
            id: 'power-tools',
            label: 'Power & Industrial Tools',
            icon: powerToolsIcon,
            isImage: true,
            path: '/power_tools'
        },
        {
            id: 'sustainable-solutions',
            label: 'Sustainable Solutions',
            icon: sustainableIcon,
            isImage: true,
            path: '/sustainable_solutions'
        },
        {
            id: 'hygienic-solutions',
            label: 'Hygienic Solutions',
            icon: hygienicIcon,
            isImage: true,
            path: '/hygienic_solutions'
        },
        {
            id: 'conversions',
            label: 'Engineering Conversions',
            icon: Calculator,
            path: '/conversion'
        },
        {
            id: 'analytics',
            label: 'Amazon Products',
            icon: BarChart3,
            path: '/amazon_products'
        },
        {
            id: 'team',
            label: 'Team Management',
            icon: Users,
            path: '/team_management'
        },
        {
            id: 'user-management',
            label: 'User Management',
            icon: UserCog,
            path: '/users_management'
        },
    ];

    const filteredMenuItems = menuItems.filter(item => {
        if (item.id === 'team' && user?.role === 'user') return false;
        if (item.id === 'settings' && user?.role !== 'admin') return false;
        return true;
    });

    const getUserInitials = () => {
        if (!user) return 'U';
        const firstInitial = user.firstName ? user.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = user.lastName ? user.lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial || user.email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (!user) return 'Utilisateur';
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.email;
    };

    const showNotification = (message, type = 'success', duration = 4000) => {
        const id = Date.now() + Math.random();
        const notification = { id, message, type, duration };
        setNotifications(prev => [...prev, notification]);
        setTimeout(() => removeNotification(id), duration);
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [section, field] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleCategorySelect = (categoryId) => {
        setSelectedCategory(categoryId);
        const category = supportCategories.find(c => c.id === categoryId);
        if (category && category.urgency === 'high') {
            setPriority('high');
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = files.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: file.size,
            type: file.type,
            file: file
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
    };

    const removeAttachment = (id) => {
        setAttachments(prev => prev.filter(att => att.id !== id));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormError('');

        try {
            if (!selectedCategory || !formData.subject || !formData.description) {
                throw new Error('Please fill in all required fields');
            }

            const ticketData = {
                category: selectedCategory,
                priority,
                subject: formData.subject,
                description: formData.description,
                contactMethod,
                contactInfo: formData.contactInfo,
                systemInfo: formData.systemInfo
            };

            // Submit ticket using your API service
            const response = await ApiService.submitSupportTicket(ticketData, attachments);

            if (response.success) {
                setSubmittedTicketId(response.ticketId);
                setShowSuccessModal(true);
                showNotification('Support ticket submitted successfully! 🎉', 'success');

                // Reset form
                setFormData({
                    subject: '',
                    description: '',
                    systemInfo: formData.systemInfo, // Keep system info
                    contactInfo: {
                        phone: '',
                        preferredTime: '',
                        timezone: 'UTC+3'
                    }
                });
                setSelectedCategory('');
                setPriority('medium');
                setAttachments([]);
            }
        } catch (error) {
            setFormError(error.message || 'Error submitting support ticket');
            showNotification('Error submitting ticket: ' + error.message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to log out?')) {
            // Handle logout
            console.log('Logout');
        }
    };

    const NotificationItem = ({ notification, onRemove }) => {
        const getNotificationStyle = (type) => {
            const baseStyle = {
                padding: '16px 20px',
                borderRadius: '12px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
                minWidth: '350px',
                maxWidth: '500px',
                position: 'relative',
                animation: 'slideInRight 0.3s ease-out',
                cursor: 'pointer'
            };

            const styles = {
                success: { ...baseStyle, backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' },
                error: { ...baseStyle, backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' },
                warning: { ...baseStyle, backgroundColor: '#fffbeb', borderColor: '#fed7aa', color: '#d97706' },
                info: { ...baseStyle, backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' }
            };

            return styles[type] || styles.info;
        };

        const getIcon = (type) => {
            const iconStyle = { width: '20px', height: '20px' };
            switch (type) {
                case 'success': return <CheckCircle style={iconStyle} />;
                case 'error': return <AlertCircle style={iconStyle} />;
                case 'warning': return <AlertCircle style={iconStyle} />;
                default: return <Bell style={iconStyle} />;
            }
        };

        return (
            <div
                style={getNotificationStyle(notification.type)}
                onClick={() => onRemove(notification.id)}
            >
                {getIcon(notification.type)}
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                        {notification.message}
                    </p>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(notification.id);
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        opacity: 0.7
                    }}
                >
                    <X style={{ width: '16px', height: '16px' }} />
                </button>
            </div>
        );
    };

    if (!user) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column'
            }}>
                <RefreshCw style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '16px' }}>Loading...</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            {/* SUCCESS MODAL */}
            {showSuccessModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '32px',
                        width: '90%', maxWidth: '500px', textAlign: 'center',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            width: '64px', height: '64px', backgroundColor: '#059669',
                            borderRadius: '50%', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', margin: '0 auto 24px'
                        }}>
                            <CheckCircle2 style={{ width: '32px', height: '32px', color: 'white' }} />
                        </div>

                        <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: '600', color: '#111827' }}>
                            Ticket Submitted Successfully!
                        </h2>

                        <p style={{ margin: '0 0 24px 0', fontSize: '16px', color: '#6b7280', lineHeight: '1.5' }}>
                            Your support request has been received. Our technical team will review your case and respond within 24 hours.
                        </p>

                        <div style={{
                            backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '8px',
                            padding: '16px', marginBottom: '24px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                                <Hash style={{ width: '16px', height: '16px', color: '#0ea5e9' }} />
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#0ea5e9' }}>TICKET ID</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', fontFamily: 'monospace', color: '#0c4a6e' }}>
                                {submittedTicketId}
                            </p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                                Save this ID for reference
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowSuccessModal(false)}
                                style={{
                                    flex: 1, padding: '12px 20px', border: '1px solid #d1d5db',
                                    background: 'white', color: '#374151', borderRadius: '8px',
                                    fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(submittedTicketId);
                                    showNotification('Ticket ID copied to clipboard!', 'info', 2000);
                                }}
                                style={{
                                    flex: 1, padding: '12px 20px', border: 'none',
                                    background: '#0ea5e9', color: 'white', borderRadius: '8px',
                                    fontSize: '14px', fontWeight: '500', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                                }}
                            >
                                <Copy style={{ width: '16px', height: '16px' }} />
                                Copy Ticket ID
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <div
                        className="sidebar-logo-container"
                        onClick={() => navigate('/dashboard')}
                        style={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            borderRadius: '8px',
                            padding: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Retour au Dashboard"
                    >
                        <div className="sidebar-logo">
                            <Building style={{ width: '24px', height: '24px', color: 'white' }} />
                        </div>
                        <div className="sidebar-brand">
                            <h1 className="sidebar-title">UNITECH IKK</h1>
                            <p className="sidebar-subtitle">Enterprise Dashboard</p>
                        </div>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {filteredMenuItems.map((item) => (
                        <div
                            key={item.id}
                            className={`nav-item ${activeMenuItem === item.id ? 'active' : ''}`}
                            onClick={() => {
                                setActiveMenuItem(item.id);
                                navigate(item.path);
                            }}
                        >
                            {item.isImage ? (
                                <img
                                    src={item.icon}
                                    alt={item.label}
                                    className="nav-icon-image"
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        display: 'block'
                                    }}
                                />
                            ) : (
                                <item.icon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        display: 'block'
                                    }}
                                />
                            )}
                            <span className="nav-text">{item.label}</span>
                        </div>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-profile">
                        <div className="user-avatar">{getUserInitials()}</div>
                        <div className="user-info">
                            <p className="user-name">{getDisplayName()}</p>
                            <p className="user-role">{user?.role || 'Utilisateur'}</p>
                        </div>
                        <button
                            className="logout-button"
                            onClick={handleLogout}
                            title="Se déconnecter"
                        >
                            <LogOut style={{ width: '16px', height: '16px' }} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                {/* Header */}
                <header className="header">
                    <div className="header-content">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                            >
                                {sidebarOpen ?
                                    <X style={{ width: '20px', height: '20px' }} /> :
                                    <Menu style={{ width: '20px', height: '20px' }} />
                                }
                            </button>

                            <nav className="breadcrumb">
                                <div className="breadcrumb-item">
                                    <Home style={{ width: '16px', height: '16px' }} />
                                    <span>Dashboard</span>
                                </div>
                                <ChevronRight style={{ width: '14px', height: '14px' }} />
                                <div className="breadcrumb-item">
                                    <LifeBuoy style={{ width: '16px', height: '16px' }} />
                                    <span>Technical Support</span>
                                </div>
                                <ChevronRight style={{ width: '14px', height: '14px' }} />
                                <div className="breadcrumb-item">
                                    <span>Submit Ticket</span>
                                </div>
                            </nav>
                        </div>

                        <div className="header-right">
                            <div className="search-container">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Search className="search-icon" style={{ width: '18px', height: '18px' }} />
                            </div>

                            <div className="header-actions">
                                <button
                                    className="header-button"
                                    title="Refresh"
                                >
                                    <RefreshCw style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    title="Notifications"
                                >
                                    <Bell style={{ width: '18px', height: '18px' }} />
                                    {notifications.length > 0 && (
                                        <span className="notification-badge">{notifications.length}</span>
                                    )}
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/settings')}
                                    title="Paramètres système"
                                >
                                    <Settings style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/project_management')}
                                    title="Gestion de projets"
                                >
                                    <Folder style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/technical_support')}
                                    title="Technical Support"
                                >
                                    <MonitorCog style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/admin_support_it')}
                                    title="Admin Support IT"
                                >
                                    <SquareActivity style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/mytickets')}
                                    title="My Tickets"
                                >
                                    <Tickets style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/quotations')}
                                    title="Quotation System"
                                >
                                    <Receipt style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/clients')}
                                    title="Clients"
                                >
                                    <Briefcase style={{ width: '18px', height: '18px' }} />
                                </button>

                                <div className="time-display">
                                    {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Support Content */}
                <div style={{ padding: '32px' }}>
                    {/* Page Header */}
                    <div style={{ marginBottom: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                backgroundColor: '#1e40af',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <LifeBuoy style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                            <div>
                                <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '700', color: '#111827' }}>
                                    Technical Support
                                </h1>
                                <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>
                                    Get expert assistance from our technical team. We're here to help you resolve any issues quickly and efficiently.
                                </p>
                            </div>
                        </div>

                        {/* Quick Contact Options */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px',
                            marginBottom: '32px'
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                textAlign: 'center',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#059669',
                                    borderRadius: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px'
                                }}>
                                    <Mail style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                    Email Support
                                </h3>
                                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280' }}>
                                    muhammad.almohtadi@ikkgroup.com
                                </p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                                    Response within 4-24 hours
                                </p>
                            </div>

                            <div style={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                textAlign: 'center',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#dc2626',
                                    borderRadius: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px'
                                }}>
                                    <Phone style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                    Phone Support
                                </h3>
                                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280' }}>
                                    +961 70 735 338
                                </p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                                    Mon-Fri 9:00-18:00 (GMT+3)
                                </p>
                            </div>

                            <div style={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                textAlign: 'center',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#7c3aed',
                                    borderRadius: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px'
                                }}>
                                    <MessageCircle style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                    Live Chat
                                </h3>
                                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280' }}>
                                    Available now
                                </p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                                    Instant response
                                </p>
                            </div>

                            <div style={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                textAlign: 'center',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#f59e0b',
                                    borderRadius: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px'
                                }}>
                                    <Video style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                    Screen Share
                                </h3>
                                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280' }}>
                                    Remote assistance
                                </p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                                    By appointment
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Support Form */}
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        border: '1px solid #e5e7eb',
                        overflow: 'hidden'
                    }}>
                        {/* Form Header */}
                        <div style={{
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid #e5e7eb',
                            padding: '24px 32px'
                        }}>
                            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600', color: '#111827' }}>
                                Submit Support Ticket
                            </h2>
                            <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>
                                Describe your issue in detail and our technical team will assist you promptly.
                            </p>
                        </div>

                        {/* Form Error */}
                        {formError && (
                            <div style={{
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                color: '#dc2626',
                                padding: '16px 32px',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <AlertCircle style={{ width: '16px', height: '16px' }} />
                                {formError}
                            </div>
                        )}

                        {/* Form Content */}
                        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
                            {/* Category Selection */}
                            <div style={{ marginBottom: '32px' }}>
                                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                                    What type of issue are you experiencing?
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                    gap: '16px'
                                }}>
                                    {supportCategories.map((category) => (
                                        <div
                                            key={category.id}
                                            onClick={() => handleCategorySelect(category.id)}
                                            style={{
                                                border: selectedCategory === category.id ? `2px solid ${category.color}` : '2px solid #e5e7eb',
                                                borderRadius: '12px',
                                                padding: '20px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                backgroundColor: selectedCategory === category.id ? `${category.color}10` : 'white'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (selectedCategory !== category.id) {
                                                    e.currentTarget.style.borderColor = category.color;
                                                    e.currentTarget.style.backgroundColor = `${category.color}05`;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedCategory !== category.id) {
                                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                                    e.currentTarget.style.backgroundColor = 'white';
                                                }
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                                <div style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    backgroundColor: category.color,
                                                    borderRadius: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <div style={{ color: 'white' }}>
                                                        {category.icon}
                                                    </div>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <h4 style={{
                                                        margin: '0 0 8px 0',
                                                        fontSize: '16px',
                                                        fontWeight: '600',
                                                        color: '#111827'
                                                    }}>
                                                        {category.name}
                                                    </h4>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: '14px',
                                                        color: '#6b7280',
                                                        lineHeight: '1.5'
                                                    }}>
                                                        {category.description}
                                                    </p>
                                                    <div style={{
                                                        marginTop: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}>
                                                        <div style={{
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: category.urgency === 'high' ? '#fef2f2' :
                                                                category.urgency === 'medium' ? '#fffbeb' : '#f0fdf4',
                                                            color: category.urgency === 'high' ? '#dc2626' :
                                                                category.urgency === 'medium' ? '#d97706' : '#059669'
                                                        }}>
                                                            {category.urgency.toUpperCase()} PRIORITY
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Subject and Description */}
                            {selectedCategory && (
                                <div style={{ marginBottom: '32px' }}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 1fr',
                                        gap: '24px',
                                        marginBottom: '24px'
                                    }}>
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Subject *
                                            </label>
                                            <input
                                                type="text"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleInputChange}
                                                placeholder="Brief description of your issue"
                                                required
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    fontSize: '14px',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    transition: 'border-color 0.2s ease'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#1e40af'}
                                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                            />
                                        </div>

                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Priority
                                            </label>
                                            <select
                                                value={priority}
                                                onChange={(e) => setPriority(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    fontSize: '14px',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    backgroundColor: 'white'
                                                }}
                                            >
                                                <option value="low">Low - General inquiry</option>
                                                <option value="medium">Medium - Standard issue</option>
                                                <option value="high">High - Urgent issue</option>
                                                <option value="critical">Critical - System down</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: '#374151',
                                            marginBottom: '8px'
                                        }}>
                                            Description *
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder="Please provide detailed information about your issue, including:&#10;• What you were trying to do&#10;• What happened instead&#10;• Any error messages you received&#10;• Steps you've already tried"
                                            required
                                            rows="8"
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none',
                                                boxSizing: 'border-box',
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                                lineHeight: '1.5',
                                                transition: 'border-color 0.2s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#1e40af'}
                                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                        />
                                        <p style={{
                                            margin: '8px 0 0 0',
                                            fontSize: '12px',
                                            color: '#6b7280'
                                        }}>
                                            The more details you provide, the faster we can help resolve your issue.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* File Attachments */}
                            {selectedCategory && (
                                <div style={{ marginBottom: '32px' }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        marginBottom: '12px'
                                    }}>
                                        Attachments (optional)
                                    </label>

                                    <div style={{
                                        border: '2px dashed #d1d5db',
                                        borderRadius: '12px',
                                        padding: '32px',
                                        textAlign: 'center',
                                        backgroundColor: '#f9fafb',
                                        transition: 'all 0.2s ease',
                                        position: 'relative'
                                    }}
                                         onDragOver={(e) => {
                                             e.preventDefault();
                                             e.currentTarget.style.borderColor = '#1e40af';
                                             e.currentTarget.style.backgroundColor = '#eff6ff';
                                         }}
                                         onDragLeave={(e) => {
                                             e.preventDefault();
                                             e.currentTarget.style.borderColor = '#d1d5db';
                                             e.currentTarget.style.backgroundColor = '#f9fafb';
                                         }}
                                         onDrop={(e) => {
                                             e.preventDefault();
                                             e.currentTarget.style.borderColor = '#d1d5db';
                                             e.currentTarget.style.backgroundColor = '#f9fafb';
                                             const files = Array.from(e.dataTransfer.files);
                                             const fileInput = { target: { files } };
                                             handleFileUpload(fileInput);
                                         }}
                                    >
                                        <input
                                            type="file"
                                            multiple
                                            onChange={handleFileUpload}
                                            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.log"
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                opacity: 0,
                                                cursor: 'pointer'
                                            }}
                                        />
                                        <Upload style={{
                                            width: '48px',
                                            height: '48px',
                                            margin: '0 auto 16px',
                                            color: '#6b7280'
                                        }} />
                                        <h4 style={{
                                            margin: '0 0 8px 0',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            color: '#374151'
                                        }}>
                                            Drop files here or click to browse
                                        </h4>
                                        <p style={{
                                            margin: 0,
                                            fontSize: '14px',
                                            color: '#6b7280'
                                        }}>
                                            Screenshots, error logs, documents (Max 10MB per file)
                                        </p>
                                        <p style={{
                                            margin: '8px 0 0 0',
                                            fontSize: '12px',
                                            color: '#9ca3af'
                                        }}>
                                            Supported: JPG, PNG, PDF, DOC, TXT, LOG
                                        </p>
                                    </div>

                                    {/* Attachment List */}
                                    {attachments.length > 0 && (
                                        <div style={{ marginTop: '16px' }}>
                                            <h5 style={{
                                                margin: '0 0 12px 0',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151'
                                            }}>
                                                Attached Files ({attachments.length})
                                            </h5>
                                            <div style={{ display: 'grid', gap: '8px' }}>
                                                {attachments.map((attachment) => (
                                                    <div
                                                        key={attachment.id}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            padding: '12px 16px',
                                                            backgroundColor: 'white',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '8px'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            <Paperclip style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                                                            <div>
                                                                <p style={{
                                                                    margin: 0,
                                                                    fontSize: '14px',
                                                                    fontWeight: '500',
                                                                    color: '#374151'
                                                                }}>
                                                                    {attachment.name}
                                                                </p>
                                                                <p style={{
                                                                    margin: 0,
                                                                    fontSize: '12px',
                                                                    color: '#6b7280'
                                                                }}>
                                                                    {(attachment.size / 1024 / 1024).toFixed(2)} MB
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeAttachment(attachment.id)}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                padding: '4px',
                                                                borderRadius: '4px',
                                                                color: '#dc2626'
                                                            }}
                                                            title="Remove file"
                                                        >
                                                            <X style={{ width: '16px', height: '16px' }} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Contact Preferences */}
                            {selectedCategory && (
                                <div style={{ marginBottom: '32px' }}>
                                    <h3 style={{
                                        margin: '0 0 16px 0',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#111827'
                                    }}>
                                        Contact Preferences
                                    </h3>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '24px',
                                        marginBottom: '24px'
                                    }}>
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Preferred Contact Method
                                            </label>
                                            <select
                                                value={contactMethod}
                                                onChange={(e) => setContactMethod(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    fontSize: '14px',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    backgroundColor: 'white'
                                                }}
                                            >
                                                <option value="email">Email</option>
                                                <option value="phone">Phone</option>
                                                <option value="both">Email + Phone</option>
                                            </select>
                                        </div>

                                        {(contactMethod === 'phone' || contactMethod === 'both') && (
                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '8px'
                                                }}>
                                                    Phone Number
                                                </label>
                                                <input
                                                    type="tel"
                                                    name="contactInfo.phone"
                                                    value={formData.contactInfo.phone}
                                                    onChange={handleInputChange}
                                                    placeholder="+961 3 123 456"
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        outline: 'none',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {(contactMethod === 'phone' || contactMethod === 'both') && (
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '24px'
                                        }}>
                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '8px'
                                                }}>
                                                    Preferred Call Time
                                                </label>
                                                <select
                                                    name="contactInfo.preferredTime"
                                                    value={formData.contactInfo.preferredTime}
                                                    onChange={handleInputChange}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        outline: 'none',
                                                        boxSizing: 'border-box',
                                                        backgroundColor: 'white'
                                                    }}
                                                >
                                                    <option value="">Any time during business hours</option>
                                                    <option value="morning">Morning (9:00-12:00)</option>
                                                    <option value="afternoon">Afternoon (12:00-15:00)</option>
                                                    <option value="evening">Evening (15:00-18:00)</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '8px'
                                                }}>
                                                    Timezone
                                                </label>
                                                <select
                                                    name="contactInfo.timezone"
                                                    value={formData.contactInfo.timezone}
                                                    onChange={handleInputChange}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        outline: 'none',
                                                        boxSizing: 'border-box',
                                                        backgroundColor: 'white'
                                                    }}
                                                >
                                                    <option value="UTC+3">UTC+3 (Beirut Time)</option>
                                                    <option value="UTC+0">UTC+0 (GMT)</option>
                                                    <option value="UTC+1">UTC+1 (CET)</option>
                                                    <option value="UTC+2">UTC+2 (EET)</option>
                                                    <option value="UTC+4">UTC+4 (GST)</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* System Information */}
                            {selectedCategory && (
                                <div style={{ marginBottom: '32px' }}>
                                    <h3 style={{
                                        margin: '0 0 16px 0',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#111827'
                                    }}>
                                        System Information
                                    </h3>
                                    <p style={{
                                        margin: '0 0 16px 0',
                                        fontSize: '14px',
                                        color: '#6b7280'
                                    }}>
                                        This information helps us diagnose technical issues more effectively.
                                    </p>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: '16px',
                                        padding: '20px',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '12px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                color: '#6b7280',
                                                marginBottom: '4px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>
                                                Browser
                                            </label>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                color: '#374151',
                                                fontFamily: 'monospace'
                                            }}>
                                                {formData.systemInfo.browser}
                                            </p>
                                        </div>

                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                color: '#6b7280',
                                                marginBottom: '4px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>
                                                Operating System
                                            </label>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                color: '#374151',
                                                fontFamily: 'monospace'
                                            }}>
                                                {formData.systemInfo.operatingSystem}
                                            </p>
                                        </div>

                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                color: '#6b7280',
                                                marginBottom: '4px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>
                                                Screen Resolution
                                            </label>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                color: '#374151',
                                                fontFamily: 'monospace'
                                            }}>
                                                {formData.systemInfo.screenResolution}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Submit Button */}
                            {selectedCategory && (
                                <div style={{
                                    paddingTop: '24px',
                                    borderTop: '1px solid #e5e7eb',
                                    display: 'flex',
                                    gap: '16px',
                                    justifyContent: 'flex-end'
                                }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedCategory('');
                                            setFormData({
                                                subject: '',
                                                description: '',
                                                systemInfo: formData.systemInfo,
                                                contactInfo: {
                                                    phone: '',
                                                    preferredTime: '',
                                                    timezone: 'UTC+3'
                                                }
                                            });
                                            setAttachments([]);
                                            setPriority('medium');
                                            setContactMethod('email');
                                        }}
                                        style={{
                                            padding: '12px 24px',
                                            border: '1px solid #d1d5db',
                                            background: 'white',
                                            color: '#374151',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#f9fafb';
                                            e.target.style.borderColor = '#9ca3af';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'white';
                                            e.target.style.borderColor = '#d1d5db';
                                        }}
                                    >
                                        Clear Form
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting || !selectedCategory || !formData.subject || !formData.description}
                                        style={{
                                            padding: '12px 32px',
                                            border: 'none',
                                            background: (isSubmitting || !selectedCategory || !formData.subject || !formData.description)
                                                ? '#9ca3af' : '#1e40af',
                                            color: 'white',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: (isSubmitting || !selectedCategory || !formData.subject || !formData.description)
                                                ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSubmitting && selectedCategory && formData.subject && formData.description) {
                                                e.target.style.backgroundColor = '#1e3a8a';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSubmitting && selectedCategory && formData.subject && formData.description) {
                                                e.target.style.backgroundColor = '#1e40af';
                                            }
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                                                Submitting Ticket...
                                            </>
                                        ) : (
                                            <>
                                                <Send style={{ width: '16px', height: '16px' }} />
                                                Submit Support Ticket
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Help Resources */}
                    <div style={{ marginTop: '32px' }}>
                        <h2 style={{
                            margin: '0 0 24px 0',
                            fontSize: '24px',
                            fontWeight: '600',
                            color: '#111827'
                        }}>
                            Self-Help Resources
                        </h2>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '20px'
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                padding: '24px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    backgroundColor: '#eff6ff',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '16px'
                                }}>
                                    <BookOpen style={{ width: '24px', height: '24px', color: '#1e40af' }} />
                                </div>
                                <h3 style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#111827'
                                }}>
                                    Knowledge Base
                                </h3>
                                <p style={{
                                    margin: '0 0 16px 0',
                                    fontSize: '14px',
                                    color: '#6b7280',
                                    lineHeight: '1.5'
                                }}>
                                    Browse our comprehensive documentation and frequently asked questions.
                                </p>
                                <button style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#1e40af',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    Browse Articles
                                    <ExternalLink style={{ width: '14px', height: '14px' }} />
                                </button>
                            </div>

                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                padding: '24px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    backgroundColor: '#f0fdf4',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '16px'
                                }}>
                                    <Video style={{ width: '24px', height: '24px', color: '#059669' }} />
                                </div>
                                <h3 style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#111827'
                                }}>
                                    Video Tutorials
                                </h3>
                                <p style={{
                                    margin: '0 0 16px 0',
                                    fontSize: '14px',
                                    color: '#6b7280',
                                    lineHeight: '1.5'
                                }}>
                                    Watch step-by-step video guides for common tasks and features.
                                </p>
                                <button style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#059669',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    Watch Videos
                                    <ExternalLink style={{ width: '14px', height: '14px' }} />
                                </button>
                            </div>

                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                padding: '24px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    backgroundColor: '#fef3c7',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '16px'
                                }}>
                                    <Users style={{ width: '24px', height: '24px', color: '#d97706' }} />
                                </div>
                                <h3 style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#111827'
                                }}>
                                    Community Forum
                                </h3>
                                <p style={{
                                    margin: '0 0 16px 0',
                                    fontSize: '14px',
                                    color: '#6b7280',
                                    lineHeight: '1.5'
                                }}>
                                    Connect with other users and share solutions in our community.
                                </p>
                                <button style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#d97706',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    Join Discussion
                                    <ExternalLink style={{ width: '14px', height: '14px' }} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Notifications Container */}
            {notifications.length > 0 && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 9999,
                    pointerEvents: 'none'
                }}>
                    <div style={{ pointerEvents: 'auto' }}>
                        {notifications.map(notification => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onRemove={removeNotification}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TechnicalSupportPage;